<template>
	<v-dialog v-model="createDutyStore.isOpen" max-width="1000" persistent>
		<VForm ref="mainFormRef" lazy-validation>
			<v-card class="create-duty-modal" elevation="4" rounded="lg">
				<div class="modal-header d-flex align-items-center justify-space-between">
					<span class="modal-title"
						>{{
							createDutyStore.mode === Mode.Planning
								? t('planning_duty_create')
								: !createDutyStore.isEditMode
									? t('planning_create_duty_template')
									: t('planning_edit_duty_template')
						}}
						<span v-if="createDutyStore.selectedDate"> ({{ formatDate(createDutyStore.selectedDate) }}) </span>
					</span>
					<v-btn icon variant="text" size="small" @click="createDutyStore.closeModal">
						<v-icon size="20">mdi-close</v-icon>
					</v-btn>
				</div>

				<v-divider />

				<v-card-text class="modal-body">
					<!-- Top "Create as" banner (not collapsible) -->
					<div class="top-banner-card">
						<v-row dense>
							<v-col cols="4">
								<v-select
									v-model="createDutyStore.form.type"
									:items="dutyTypeOptions"
									:label="t('create_as')"
									variant="underlined"
									density="compact"
									hide-details
									:menu-props="{ contentClass: '' }"
									:disabled="createDutyStore.isEditMode"
									@update:model-value="onFormTypeChange"
								/>
							</v-col>

							<template v-if="createDutyStore.form.type === CREATE_DUTY_FORM_TYPE.COPY">
								<v-col cols="4">
									<v-select
										v-model="createDutyStore.form.selectDutyFrom"
										:items="DUTY_TEMPLATE_SOURCES"
										item-title="title"
										item-value="value"
										:label="t('select_duty_from')"
										variant="underlined"
										density="compact"
										hide-details
										:menu-props="{ contentClass: '' }"
									/>
								</v-col>

								<v-col cols="4">
									<v-autocomplete
										v-model="createDutyStore.form.dutyOrDriverSelection"
										:items="
											createDutyStore.form.selectDutyFrom === 'Recently created'
												? recentDutiesOptions
												: createDutyStore.form.selectDutyFrom === 'Current day'
													? currentDayOptions
													: createDutyStore.form.selectDutyFrom === 'Driver'
														? driverCategorizedOptions
														: createDutyStore.form.selectDutyFrom === 'Template'
															? systemTemplatesOptions
															: []
										"
										:item-title="getDutyOrDriverTitle"
										return-object
										:label="createDutyStore.form.selectDutyFrom === 'Driver' ? t('driver') : t('duty')"
										variant="underlined"
										density="compact"
										hide-details
										@update:model-value="(selectedItem) => applySelectedTemplateToForm(selectedItem)"
										:menu-props="{ contentClass: '' }"
									/>
								</v-col>
							</template>
						</v-row>
					</div>

					<!-- META DATA -->
					<fieldset :disabled="areFormFieldsDisabled" class="form-fields-fieldset">
						<div class="dropdown-container">
							<div class="section-toggle-header d-flex align-center" @click="isMetaExpanded = !isMetaExpanded">
								<img contain src="@/assets/meta-data.svg" width="20" height="20" />
								<span class="toggle-title">{{ t('meta_data') }}</span>
								<v-spacer />
								<v-icon size="18">{{ isMetaExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
							</div>

							<v-expand-transition>
								<div v-show="isMetaExpanded" class="toggle-content">
									<div class="toggle-content-wrapper">
										<div class="inner-section-subtitle">{{ t('general_data') }}</div>
										<div class="form-section-card">
											<v-row dense>
												<v-col cols="6">
													<v-text-field
														v-model="createDutyStore.form.name"
														variant="underlined"
														density="compact"
														:rules="[(v) => !!v || t('planning_not_empty')]"
													>
														<template #label>
															<span>{{ t('duty_name') }} <span class="required-star">*</span></span>
														</template>
													</v-text-field>
												</v-col>

												<v-col cols="6">
													<v-autocomplete
														v-model="createDutyStore.form.qualifications"
														:items="qualificationBridge.activeQualifications"
														:label="$t('planning_required_qualifications')"
														:closable-chips="true"
														item-title="name"
														item-value="id"
														multiple
														chips
														variant="underlined"
														density="compact"
														:menu-props="{ contentClass: '' }"
														:disabled="createDutyStore.isTypeCopy"
													>
														<template v-slot:divider="{ props }">
															<div class="d-flex ga-4 align-center">
																<v-divider></v-divider>
																{{ props.text }}
																<v-divider></v-divider>
															</div>
														</template>

														<template #prepend-item>
															<v-list-item
																:title="allQualificationsSelected ? $t('Unselect_all') : $t('action_select_all')"
																@click="toggleSelectAllQualifications"
															>
																<template #prepend>
																	<v-checkbox-btn
																		:model-value="allQualificationsSelected"
																		:indeterminate="someQualificationsSelected && !allQualificationsSelected"
																		color="primary"
																	></v-checkbox-btn>
																</template>
															</v-list-item>
															<v-divider class="mb-2" />
														</template>
													</v-autocomplete>
												</v-col>

												<v-col
													v-if="createDutyStore.mode === Mode.Planning && createDutyStore.form.driverName"
													cols="6"
													class="d-flex align-start"
												>
													<v-text-field
														v-model="createDutyStore.form.driverName"
														:label="t('driver')"
														variant="underlined"
														density="compact"
														disabled
													/>
												</v-col>

												<v-col
													v-if="
														createDutyStore.mode === Mode.Planning &&
														createDutyStore.form.type === CREATE_DUTY_FORM_TYPE.COPY &&
														planningBridge.editor.superuser
													"
													cols="6"
												>
													<v-text-field
														v-model="createDutyStore.form.root"
														:label="t('root_duty')"
														variant="underlined"
														density="compact"
														disabled
													/>
												</v-col>
												<v-col v-if="planningBridge.editor.superuser" cols="6">
													<v-text-field
														v-model="createDutyStore.form.provider"
														:label="t('user_field_provider')"
														variant="underlined"
														density="compact"
														disabled
													/>
												</v-col>
												<v-col v-if="planningBridge.editor.superuser" cols="6">
													<v-text-field
														v-model="createDutyStore.form.providerId"
														:label="t('user_field_providerId')"
														variant="underlined"
														density="compact"
														disabled
													/>
												</v-col>
											</v-row>
										</div>
									</div>
								</div>
							</v-expand-transition>
						</div>

						<!-- BASIC DATA (time + breaks) -->
						<div class="dropdown-container">
							<div class="section-toggle-header d-flex align-center" @click="isBasicExpanded = !isBasicExpanded">
								<img contain src="@/assets/basic-data.svg" width="20" height="20" class="ml-1" />
								<span class="toggle-title">{{ t('basic_data') }}</span>
								<v-spacer />
								<v-checkbox
									v-if="createDutyStore.mode === Mode.Template"
									v-model="createDutyStore.hasBasicData"
									@update:model-value="onHasBasicDataToggle"
									@click.stop
									density="compact"
									hide-details
									color="#115151"
									class="mt-0"
								>
									<template #label>
										<span>
											{{ t('include_basic_data') }}
											<v-tooltip open-delay="500" :max-width="382" :text="t('template_partial_basic_data_info')">
												<template #activator="{ props }"
													><v-icon v-bind="props" size="18" color="blue" class="ml-1"> mdi-information-outline </v-icon>
												</template>
											</v-tooltip>
										</span>
									</template>
								</v-checkbox>
								<v-icon size="18">{{ isBasicExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
							</div>

							<v-expand-transition>
								<div v-show="isBasicExpanded" class="toggle-content">
									<div class="toggle-content-wrapper">
										<div class="inner-section-subtitle">{{ t('basic_time_data') }}</div>
										<div class="form-section-card">
											<v-col
												v-if="createDutyStore.mode === Mode.Template && createDutyStore.form.type === CREATE_DUTY_FORM_TYPE.WORK_DAY"
												cols="6"
												class="d-flex align-center ps-0"
											>
											</v-col>

											<v-row dense>
												<v-col cols="6">
													<v-text-field
														v-model="createDutyStore.form.startStop"
														:label="t('start_stop')"
														variant="underlined"
														density="compact"
														:disabled="createDutyStore.isTimeFieldDisabled"
													/>
												</v-col>
												<v-col cols="6">
													<v-text-field
														v-model="createDutyStore.form.endStop"
														:label="t('end_stop')"
														variant="underlined"
														density="compact"
														:disabled="createDutyStore.isTimeFieldDisabled"
													/>
												</v-col>
												<v-col cols="6">
													<TimeEntryForm
														v-model="createDutyStore.form.start"
														:label="t('start_time')"
														append-inner-icon="mdi-clock-outline"
														variant="underlined"
														density="compact"
														:disabled="createDutyStore.isTimeFieldDisabled"
														:rules="timeRuleFactory('start')"
													/>
												</v-col>
												<v-col cols="6">
													<TimeEntryForm
														v-model="createDutyStore.form.end"
														:label="t('end_time')"
														append-inner-icon="mdi-clock-outline"
														variant="underlined"
														density="compact"
														:disabled="createDutyStore.isTimeFieldDisabled"
														:rules="timeRuleFactory('end')"
													/>
												</v-col>

												<v-col cols="6">
													<TimeEntryForm
														v-model="createDutyStore.form.standbyStart"
														:label="t('standby_start')"
														append-inner-icon="mdi-clock-outline"
														variant="underlined"
														density="compact"
														:disabled="createDutyStore.isTimeFieldDisabled"
														:rules="timeRuleFactory('standbyStart')"
													/>
												</v-col>
												<v-col cols="6">
													<TimeEntryForm
														v-model="createDutyStore.form.standbyEnd"
														:label="t('standby_end')"
														append-inner-icon="mdi-clock-outline"
														variant="underlined"
														density="compact"
														:disabled="createDutyStore.isTimeFieldDisabled"
														:rules="timeRuleFactory('standbyEnd')"
													/>
												</v-col>
											</v-row>
										</div>

										<!-- Breaks -->
										<div class="breaks-header d-flex align-center justify-space-between">
											<span class="inner-section-subtitle mb-0">{{ t('duty.break.title') }}</span>
											<v-btn
												size="small"
												class="add-break-btn text-none"
												@click="addNewBreak"
												:disabled="createDutyStore.isTimeFieldDisabled"
											>
												{{ t('planning_add_breaks') }}
											</v-btn>
										</div>

										<VForm ref="breaksFormRef">
											<v-card v-for="(brk, idx) in createDutyStore.form.breaks" :key="idx" class="break-card mb-4" flat>
												<!-- Header -->
												<div class="break-card__header">
													<div class="break-card__title">
														<span class="break-title"> {{ t('duty.break.title') }} {{ idx + 1 }} </span>

														<span class="break-duration">
															<v-icon size="12">mdi-timer-sand</v-icon>
															{{ brk.duration || '00:00' }}
														</span>
													</div>

													<div class="break-card__actions">
														<v-switch
															v-model="brk.paid"
															:label="`${t('paid')} ${t('duty.break.title')}`"
															class="break-paid-switch"
															color="primary-green"
															density="compact"
															hide-details
															@update:model-value="onBreakTimeChange(idx)"
															:disabled="createDutyStore.isTimeFieldDisabled"
														/>

														<v-btn
															icon="mdi-close"
															size="24"
															variant="text"
															color="red-lighten-1"
															@click="removeBreakRow(idx)"
															:disabled="createDutyStore.isTimeFieldDisabled"
														/>
													</div>
												</div>

												<v-divider class="break-divider"></v-divider>

												<!-- Body -->
												<v-card-text class="break-card__body pt-4">
													<v-row>
														<v-col cols="6">
															<TimeEntryForm
																v-model="brk.start"
																:label="t('planning_start_time')"
																:rules="[
																	isTimeFormatValid,
																	createBreakTimePairRule(() => brk.end),
																	() => checkBreakOverlapRule(idx),
																	() => checkBreakStartWithinDutyRule(idx),
																]"
																@update:model-value="onBreakTimeChange(idx)"
																:disabled="createDutyStore.isTimeFieldDisabled"
															/>
														</v-col>

														<v-col cols="6">
															<TimeEntryForm
																v-model="brk.end"
																:label="t('planning_end_time')"
																:rules="[
																	isTimeFormatValid,
																	createBreakTimePairRule(() => brk.start),
																	() => checkBreakTimeOrderRule(idx),
																	() => checkBreakOverlapRule(idx),
																	() => checkBreakEndWithinDutyRule(idx),
																]"
																@update:model-value="onBreakTimeChange(idx)"
																:disabled="createDutyStore.isTimeFieldDisabled"
															/>
														</v-col>
													</v-row>
												</v-card-text>
											</v-card>
										</VForm>
									</div>
									<!-- Additional / calculated work time (highlighted) -->
									<div class="form-section-card highlight-yellow">
										<v-row class="align-center">
											<v-col cols="6" class="d-flex flex-column justify-end big-time-picker">
												<TimeEntryForm
													v-model="createDutyStore.form.additionalWorkTime"
													:label="t('additional_work_time')"
													:rules="[isTimeFormatValid]"
													append-inner-icon="mdi-clock-outline"
													variant="underlined"
													density="compact"
												/>
											</v-col>
											<v-col cols="6" class="align-self-baseline">
												<div class="worktime-calculated-card">
													<div class="worktime-calculated-label">{{ t('calculated_work_time') }}</div>
													<v-chip class="worktime-calculated-chip" size="small">{{
														createDutyStore.calculatedWorkTimeDisplay
													}}</v-chip>
												</div>
											</v-col>
										</v-row>
									</div>
								</div>
							</v-expand-transition>
						</div>

						<!-- ALLOWANCES DATA (extra pay + labels + comments) -->
						<div class="dropdown-container">
							<div class="section-toggle-header d-flex align-center" @click="isAllowancesExpanded = !isAllowancesExpanded">
								<img contain src="@/assets/allowance-data.svg" width="20" height="20" />
								<span class="toggle-title">{{ t('allowances_data') }}</span>
								<v-spacer />
								<v-icon size="18">{{ isAllowancesExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
							</div>

							<v-expand-transition>
								<div v-show="isAllowancesExpanded" class="toggle-content">
									<div class="toggle-content-wrapper">
										<div class="inner-section-subtitle">{{ t('navigation_extraPay') }}</div>
										<div class="form-section-card mb-6">
											<v-row dense>
												<v-col cols="6" v-for="field in createDutyStore.extraPayFieldsWithLabels" :key="field.accountNumber">
													<v-text-field
														v-model.number="createDutyStore.form.extraPayAccountQuantity[field.accountNumber]"
														:label="field.label"
														variant="underlined"
														density="compact"
														type="number"
													/>
												</v-col>
											</v-row>
										</div>

										<div class="inner-section-subtitle">{{ t('duty_changed_property_labels') }}</div>
										<div class="labels-wrapper mb-6">
											<LabelManager v-model="createDutyStore.form.labels" :edit="true" />
										</div>

										<div class="inner-section-subtitle">{{ t('comments') }}</div>
										<div class="form-section-card">
											<v-row dense>
												<v-col cols="6">
													<v-textarea
														v-model="createDutyStore.form.notes"
														:placeholder="t('planning_type_here')"
														rows="2"
														auto-grow
														variant="underlined"
														density="comfortable"
														persistent-placeholder
													>
														<template #label>
															<span>
																{{ t('information_for_employees') }}
															</span>

															<v-tooltip open-delay="200" :text="t('planning_note_public_info')" location="top" open-on-hover>
																<template #activator="{ props }">
																	<v-icon v-bind="props" size="18" color="blue" class="ml-1" style="pointer-events: auto">
																		mdi-information-outline
																	</v-icon>
																</template>
															</v-tooltip>
														</template>
													</v-textarea>
												</v-col>
												<v-col cols="6">
													<v-textarea
														v-model="createDutyStore.form.internalNotes"
														:placeholder="t('planning_type_here')"
														rows="2"
														auto-grow
														variant="underlined"
														density="comfortable"
														persistent-placeholder
													>
														<template #label>
															<span>
																{{ t('disposition_note') }}
															</span>

															<v-tooltip
																open-delay="200"
																:text="t('planning_note_dispatchers_info')"
																location="top"
																open-on-hover
															>
																<template #activator="{ props }">
																	<v-icon v-bind="props" size="18" color="blue" class="ml-1" style="pointer-events: auto">
																		mdi-information-outline
																	</v-icon>
																</template>
															</v-tooltip>
														</template>
													</v-textarea>
												</v-col>
											</v-row>
										</div>
									</div>
								</div>
							</v-expand-transition>
						</div>
					</fieldset>
				</v-card-text>

				<v-divider />

				<v-card-actions class="pa-4">
					<v-btn variant="text" color="#000000DE" class="px-6 font-weight-bold" @click="createDutyStore.closeModal">
						{{ t('action_cancel') }}
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn
						v-if="!isPastOrPublishedDay || !isActive"
						variant="flat"
						color="#F5F5F5"
						class="px-6 font-weight-bold"
						:disabled="isSaveDisabled"
						@click="handleSave"
					>
						<span v-if="!createDutyStore.isEditMode">{{ t('action_save_and_close') }}</span>
						<span v-else>{{ t('action_edit_and_close') }}</span>
					</v-btn>
					<v-btn
						v-if="isPastOrPublishedDay"
						variant="flat"
						color="teal-darken-4"
						:disabled="isSaveDisabled"
						:loading="previewButtonLoading"
						@click="() => handlePreviewButtonClick(() => handleSave())"
					>
						<v-progress-circular v-if="previewButtonLoading" indeterminate size="18" width="2" />
						<span v-else>{{ $t(previewButtonLabelKey) }}</span>
					</v-btn>
				</v-card-actions>
			</v-card>
		</VForm>
	</v-dialog>
</template>

<script setup lang="ts">
import { ref, nextTick, onBeforeMount, computed, watch } from 'vue';
import { VForm } from 'vuetify/components';
import { useDateTime } from '../../../../../../components/composables/useDateTime';
import { useI18n } from 'vue-i18n';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';
import { Mode, usePlanningCreateDutyStore, CREATE_DUTY_FORM_TYPE } from '@/rennovation/planning-view/store/usePlanningCreateDutyStore';
import { useQualificationBridgeStore } from '@/rennovation/planning-view/store/bridge/qualificationBridge';
import { useDutyTemplatesLoader } from '@/rennovation/planning-view/composables/useDutyTemplatesLoader';
import { parseTimeToMinutes } from '@/rennovation/shared/utils/time-utils';

import LabelManager from '../../../../../../components/common/LabelManager.vue';
import TimeEntryForm from '../../../../../../components/DateAndTime/TimeEntryForm/TimeEntryForm.vue';

import { timeFormatRule } from '@/rennovation/shared/utils/time-validation-utils';

import { useDutyStore } from '@/rennovation/planning-view/store';
import { useUserStore } from '@/rennovation/planning-view/store';
import { useDayStore } from '@/rennovation/planning-view/store/useDayStore';

import { useUserGroupBridge } from '@/rennovation/planning-view/store/useUserGroupBridge';
import { usePlanningBridgeStore } from '@/rennovation/planning-view/store/bridge/planningBridge';

type TimeFieldName = 'start' | 'end' | 'standbyStart' | 'standbyEnd';

const { t } = useI18n();
const { formatDate } = useDateTime();
const dayStore = useDayStore();

const DUTY_TEMPLATE_SOURCES = [
	{ title: t('duty_template_recent'), value: 'Recently created' },
	{ title: t('duty_template_currentDay'), value: 'Current day' },
	{ title: t('duty_template_driver'), value: 'Driver' },
	{ title: t('duty_template_template'), value: 'Template' },
];

const isPastOrPublishedDay = computed(() => dayStore.isPastOrPublishedDay(createDutyStore.selectedDate));

const isMetaExpanded = ref(true);
const isBasicExpanded = ref(true);
const isAllowancesExpanded = ref(true);

const createDutyStore = usePlanningCreateDutyStore();
const qualificationBridge = useQualificationBridgeStore();

const mainFormRef = ref<InstanceType<typeof VForm> | null>(null);
const breaksFormRef = ref<InstanceType<typeof VForm> | null>(null);

const dutyStore = useDutyStore();
const userStore = useUserStore();
const userGroupbridge = useUserGroupBridge();
const planningBridge = usePlanningBridgeStore();

const emit = defineEmits(['save']);

const dutyTypeOptions = computed(() => [
	{ title: t('duty_template_copy'), value: CREATE_DUTY_FORM_TYPE.COPY },
	{ title: t('duty_dayType_duty'), value: CREATE_DUTY_FORM_TYPE.WORK_DAY },
]);

const { isActive, previewButtonLoading, previewButtonLabelKey, handlePreviewButtonClick } = useDutyChangePreview();

const { recentDutiesOptions, currentDayOptions, driverCategorizedOptions, systemTemplatesOptions, applySelectedTemplateToForm, preloadModalData } =
	useDutyTemplatesLoader();

const isSaveDisabled = computed(() => {
	const isCopyMode = createDutyStore.form.type === CREATE_DUTY_FORM_TYPE.COPY;
	const hasSelection = !!createDutyStore.form.dutyOrDriverSelection;
	return isCopyMode && !hasSelection;
});

const selectedQualificationIds = computed<string[]>(() => {
	const currentVals = createDutyStore.form.qualifications || [];
	return currentVals.map((item: any) => (typeof item === 'object' && item !== null ? item.id || item._id : item));
});

const allQualificationsSelected = computed(() => {
	const available = qualificationBridge.activeQualifications;
	if (!available || available.length === 0) return false;
	return selectedQualificationIds.value.length === available.length;
});

const someQualificationsSelected = computed(() => {
	const selectedCount = selectedQualificationIds.value.length;
	const availableCount = qualificationBridge.activeQualifications?.length || 0;
	return selectedCount > 0 && selectedCount < availableCount;
});

const isDutySelectionValid = computed(() => {
	if (createDutyStore.isEditMode) {
		return true;
	}

	if (createDutyStore.form.type !== CREATE_DUTY_FORM_TYPE.COPY) {
		return true;
	}

	return !!createDutyStore.form.dutyOrDriverSelection;
});

const areFormFieldsDisabled = computed(() => {
	if (createDutyStore.isEditMode) {
		return false;
	}

	const type = createDutyStore.form.type;

	if (type !== CREATE_DUTY_FORM_TYPE.WORK_DAY && type !== CREATE_DUTY_FORM_TYPE.COPY) {
		return true;
	}

	if (type === CREATE_DUTY_FORM_TYPE.COPY) {
		return !isDutySelectionValid.value;
	}

	return false;
});

const getDutyOrDriverTitle = (item: any): string => {
	if (createDutyStore.form.selectDutyFrom === 'Template' && !item.raw?.hasBasicData) {
		return `${item.name} ${t('planning_missing_basic_data')}`;
	}

	return item.name;
};

const toggleSelectAllQualifications = () => {
	if (allQualificationsSelected.value) {
		createDutyStore.form.qualifications = [];
	} else {
		const available = qualificationBridge.activeQualifications || [];
		createDutyStore.form.qualifications = available.map((q: any) => q.id || q._id);
	}
};

const onHasBasicDataToggle = async (val: boolean) => {
	await nextTick();
	if (mainFormRef.value) {
		mainFormRef.value.resetValidation();
	}
};

const addNewBreak = () => {
	createDutyStore.form.breaks.push({
		paid: false,
		start: '00:00',
		end: '00:00',
		duration: '',
	});
};

const removeBreakRow = async (index: number) => {
	createDutyStore.form.breaks.splice(index, 1);
	await nextTick();
	if (breaksFormRef.value) {
		breaksFormRef.value.validate();
	}
};

const parseTime = (timeStr: string): number => {
	if (!timeStr) return 0;
	const [hours, minutes] = timeStr.split(':').map(Number);
	return hours * 60 + minutes;
};

const isTimeFormatValid = (v: string | null | undefined) => {
	if (!createDutyStore.hasBasicData || !v || !v.trim()) return true;
	return timeFormatRule(t)(v);
};

const hasAnyValueRule = () => {
	if (!createDutyStore.hasBasicData) return true;
	const { start, end, standbyStart, standbyEnd } = createDutyStore.form;
	const hasAny = [start, end, standbyStart, standbyEnd].some((val) => String(val ?? '').trim().length > 0);
	return hasAny || t('planning_at_least_one_time_pair_required');
};

const requiredIfCounterpartRuleFactory = (fieldName: TimeFieldName) => {
	const partnerMap: Record<TimeFieldName, TimeFieldName> = {
		start: 'end',
		end: 'start',
		standbyStart: 'standbyEnd',
		standbyEnd: 'standbyStart',
	};

	return (v: string | null | undefined) => {
		if (!createDutyStore.hasBasicData) return true;
		const partnerField = partnerMap[fieldName];
		const partnerValue = createDutyStore.form[partnerField];

		const hasValue = (val: unknown): boolean => {
			if (val == null) return false;
			if (typeof val === 'string') return val.trim().length > 0;
			return true;
		};

		const currentHasValue = hasValue(v);
		const partnerHasValue = hasValue(partnerValue);

		if (!currentHasValue && partnerHasValue) {
			return t('planning_duty_details_please_choose_time');
		}
		return true;
	};
};

const timeOrderRule = (fieldName: TimeFieldName) => {
	return () => {
		if (!createDutyStore.hasBasicData) return true;
		const isStandby = fieldName.startsWith('standby');
		const start = isStandby ? createDutyStore.form.standbyStart : createDutyStore.form.start;
		const end = isStandby ? createDutyStore.form.standbyEnd : createDutyStore.form.end;

		if (!start || !end) return true;
		const counterpartKey = fieldName.endsWith('Start') ? 'start' : 'end';
		return parseTime(start) <= parseTime(end) || t(`planning_start_time_must_be_before_end_time_${counterpartKey}`);
	};
};

const timeRuleFactory = (fieldName: TimeFieldName) => {
	if (!createDutyStore.hasBasicData) return [];
	return [isTimeFormatValid, hasAnyValueRule, requiredIfCounterpartRuleFactory(fieldName), timeOrderRule(fieldName)];
};

const createBreakTimePairRule = (getPartnerValue: () => string | null | undefined) => {
	return (currentValue: string | null | undefined) => {
		if (!createDutyStore.hasBasicData) return true;

		const hasCurrent = typeof currentValue === 'string' && currentValue.trim() !== '';
		const hasPartner = typeof getPartnerValue() === 'string' && getPartnerValue()!.trim() !== '';

		if (hasCurrent && !hasPartner) {
			return t('planning_duty_details_please_choose_time');
		}

		return true;
	};
};

const checkBreakTimeOrderRule = (index: number) => {
	if (!createDutyStore.hasBasicData) return true;
	const brk = createDutyStore.form.breaks[index];
	if (!brk?.start || !brk?.end) return true;

	const startMin = parseTimeToMinutes(brk.start);
	const endMin = parseTimeToMinutes(brk.end);

	return startMin <= endMin || t('planning_start_time_must_be_before_end_time_end');
};

const checkBreakOverlapRule = (currentIndex: number) => {
	if (!createDutyStore.hasBasicData) return true;
	const currentBreak = createDutyStore.form.breaks[currentIndex];
	if (!currentBreak?.start || !currentBreak?.end) return true;

	const currentStart = parseTimeToMinutes(currentBreak.start);
	const currentEnd = parseTimeToMinutes(currentBreak.end);

	for (let i = 0; i < createDutyStore.form.breaks.length; i++) {
		if (i === currentIndex) continue;

		const otherBreak = createDutyStore.form.breaks[i];
		if (!otherBreak?.start || !otherBreak?.end) continue;

		const otherStart = parseTimeToMinutes(otherBreak.start);
		const otherEnd = parseTimeToMinutes(otherBreak.end);

		if (currentStart <= otherEnd && currentEnd >= otherStart) {
			return t('breaks_overlap');
		}
	}
	return true;
};

const checkBreakStartWithinDutyRule = (index: number) => {
	if (!createDutyStore.hasBasicData) return true;
	const brk = createDutyStore.form.breaks[index];
	const dutyStart = createDutyStore.form.start;
	const dutyEnd = createDutyStore.form.end;

	if (!dutyStart || !dutyEnd || !brk?.start) return true;

	const breakStartMin = parseTimeToMinutes(brk.start);
	const dutyStartMin = parseTimeToMinutes(dutyStart);

	if (breakStartMin < dutyStartMin) return t('planning_break_starts_before_duty');
	return true;
};

const checkBreakEndWithinDutyRule = (index: number) => {
	if (!createDutyStore.hasBasicData) return true;
	const brk = createDutyStore.form.breaks[index];
	const dutyStart = createDutyStore.form.start;
	const dutyEnd = createDutyStore.form.end;

	if (!dutyStart || !dutyEnd || !brk?.end) return true;

	const breakEndMin = parseTimeToMinutes(brk.end);
	const dutyEndMin = parseTimeToMinutes(dutyEnd);

	if (breakEndMin > dutyEndMin) return t('planning_break_ends_after_duty');
	return true;
};

const onBreakTimeChange = async (index: number) => {
	const brk = createDutyStore.form.breaks[index];
	if (brk.start && brk.end) {
		const startMin = parseTimeToMinutes(brk.start);
		const endMin = parseTimeToMinutes(brk.end);

		if (endMin >= startMin) {
			const diffMinutes = endMin - startMin;
			const hours = Math.floor(diffMinutes / 60);
			const minutes = diffMinutes % 60;
			brk.duration = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
		} else {
			brk.duration = '';
		}
	} else {
		brk.duration = '';
	}

	await nextTick();
	if (breaksFormRef.value) {
		breaksFormRef.value.validate();
	}
};

const handleSave = async () => {
	if (mainFormRef.value) {
		const { valid } = await mainFormRef.value.validate();
		if (!valid) {
			return;
		}
	}
	if (breaksFormRef.value) {
		const { valid } = await breaksFormRef.value.validate();
		if (!valid) return;
	}
	let saved;
	if (!createDutyStore.isEditMode) {
		saved = await createDutyStore.handleSaveAndCreate();
		emit('save', saved);
		createDutyStore.closeModal();
		return;
	}

	emit('save', saved);
};

const isResetting = ref(false);

const onFormTypeChange = async () => {
	isResetting.value = true;

	if (createDutyStore.form.type === CREATE_DUTY_FORM_TYPE.COPY) {
		createDutyStore.resetForm();
	}

	await nextTick();

	mainFormRef.value?.resetValidation();
	breaksFormRef.value?.resetValidation();

	await nextTick();
	isResetting.value = false;
};

watch(
	() => [createDutyStore.form.start, createDutyStore.form.end, createDutyStore.form.standbyStart, createDutyStore.form.standbyEnd],
	async () => {
		if (isResetting.value) return;

		await nextTick();
		if (mainFormRef.value) {
			mainFormRef.value.validate();
		}
	}
);

onBeforeMount(async () => {
	try {
		await Promise.all([qualificationBridge.loadQualifications(), preloadModalData()]);

		if (createDutyStore.selectedDate) {
			await dutyStore.fetchAvailableDutiesByDay(createDutyStore.selectedDate);
			await userStore.loadAvailableDriversByDay(createDutyStore.selectedDate);
			await userGroupbridge.fetchUsers();
		}
	} catch (error) {
		console.error('Error during pre-mount data fetching:', error);
	}
});
</script>

<style scoped>
.create-duty-modal {
	font-family: Roboto, sans-serif;
	background-color: #fcfcfc;
}

.modal-header {
	padding: 16px 24px;

	.modal-title {
		font-size: 18px;
		font-weight: 500;
		color: #222;
	}
}

.modal-body {
	padding: 0px !important;
	max-height: 70vh;
	overflow-y: scroll;
	scrollbar-width: thin;
	background: #ffffff;

	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background-color: rgba(0, 0, 0, 0.12);
		border-radius: 10px;
	}
}

.required-star {
	color: #d32f2f;
}

.top-banner-card {
	background-color: #fff8e1;
	padding: 20px;
}

.dropdown-container {
	margin: 20px;
	border-radius: 8px;
	border: 1px solid #e0e0e0;
	background-color: #f5f5f5;
}

.section-toggle-header {
	cursor: pointer;
	user-select: none;
	transition: all 0.2s ease;
	margin-top: 16px;
	padding: 16px 20px !important;
	border-bottom: 1px solid #e0e0e0;

	img {
		margin-right: 2px;
	}

	&:first-of-type {
		margin-top: 0 !important;
		border-top: none !important;
	}

	&:hover {
		background-color: #f8fafb;
	}

	.toggle-title {
		font-size: 15px;
		font-weight: 600;
		color: #222;
	}
}

.toggle-content {
	overflow: hidden;
}

.toggle-content-wrapper {
	padding: 20px;
}

.inner-section-subtitle {
	font-size: 16px;
	font-weight: 600;
	color: #757575;
	text-transform: none;
	margin-left: 8px;
	margin-bottom: 12px;
}

.form-section-card {
	background-color: #ffffff;
	border: 1px solid #eef0f2;
	border-radius: 8px;
	padding: 16px;
	box-shadow: none !important;

	&.highlight-yellow {
		background-color: #fdf3d7;
		border: none;
	}
}

.breaks-header {
	margin: 14px 0 12px 0;

	.inner-section-subtitle {
		margin: 0;
	}
}

.labels-wrapper {
	padding: 4px 0;
}

.save-close-btn {
	background-color: #115151 !important;
	color: #ffffff !important;
}

.preview-btn {
	color: #9e9e9e !important;
	border-color: #d1d5db !important;
}

.add-break-btn {
	background-color: #eeeeee !important;
	color: #000000de !important;
	font-family: Roboto, sans-serif;
	font-weight: 500;
	font-size: 14px;
	text-align: center;
	text-transform: uppercase;
	box-shadow: none !important;
}

.break-row-item {
	background: transparent !important;
	box-shadow: none !important;
	padding: 4px 8px;
}

.worktime-calculated-card {
	background-color: #ffffff;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	padding: 12px 8px;
}

.worktime-calculated-label {
	font-size: 12px;
	font-weight: 400;
	color: #00000099;
}

.worktime-calculated-chip {
	background-color: #eeeeee !important;
	color: #000000de !important;
	font-size: 10px;
	font-weight: 500;
	border-radius: 10px;
}

.break-card {
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	overflow: hidden;
	background: #fff;
}

.break-card__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px;
}

.break-card__title {
	display: flex;
	align-items: center;
	gap: 20px;
}

.break-card__actions {
	display: flex;
	align-items: center;
	gap: 8px;
}

.break-title {
	color: #202020;
	font-family: Roboto;
	font-weight: 500;
	font-style: Medium;
	font-size: 14px;
	line-height: 150%;
	letter-spacing: 0%;
}

.break-duration {
	display: flex;
	align-items: center;
	gap: 4px;

	color: #616161;
	font-family: Roboto;
	font-weight: 400;
	font-size: 14px;
	line-height: 150%;
	letter-spacing: 0%;
}

.break-paid-switch :deep(.v-label) {
	color: #00000099;
	font-family: Roboto, sans-serif;
	font-weight: 400;
	font-size: 12px;
	line-height: 20px;
	letter-spacing: 0.4px;
}

.break-card .v-divider {
	border-color: #e0e0e0;
}

.break-divider {
	border-color: #e0e0e0;
	border-width: 1px 0 0;
	opacity: 1;
}

.break-card__body {
	background: #fafafa;
	padding: 16px;
}

:deep(.big-time-picker input),
:deep(.big-time-picker .v-input__append) {
	padding-top: 44px;
}
</style>
